"use client";

import dynamic from "next/dynamic";
import Link from "next/link";
import { createPortal } from "react-dom";
import {
  type ComponentType,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { icons } from "@/components/sidebar/nav-icons";
import { canReadDocTopic, DEFAULT_DOC_REVIEW_DATE, DOC_TOPICS, getRelatedTopics, getTopicBySlug, type DocTopic } from "@/lib/docs/registry";
import { searchDocumentation, type DocTopicMatch } from "@/lib/docs/search";
import { highlight } from "@/lib/search/match";
import { DocArticleFooter, DocTableOfContents } from "@/components/docs/doc-article-extras";

function TopicLoading() {
  return (
    <div className="space-y-3 py-4" aria-label="Loading documentation">
      <div className="h-4 w-4/5 animate-pulse rounded bg-spark-light-gray" />
      <div className="h-4 w-full animate-pulse rounded bg-spark-light-gray" />
      <div className="h-4 w-3/5 animate-pulse rounded bg-spark-light-gray" />
    </div>
  );
}

const loadTopic = (loader: () => Promise<{ default: ComponentType }>) =>
  dynamic(loader, { loading: TopicLoading });

// Each article stays in its own chunk. Opening the help dialog should not add
// the entire documentation library to the top bar's initial client bundle.
export const TOPIC_COMPONENTS: Record<string, ComponentType> = {
  "getting-started": loadTopic(() => import("@/components/docs/topics/getting-started")),
  "logging-in": loadTopic(() => import("@/components/docs/topics/logging-in")),
  "guided-tours": loadTopic(() => import("@/components/docs/topics/guided-tours")),
  "creating-a-company": loadTopic(() => import("@/components/docs/topics/creating-a-company")),
  "setting-up-a-company": loadTopic(() => import("@/components/docs/topics/setting-up-a-company")),
  "adding-integrations": loadTopic(() => import("@/components/docs/topics/adding-integrations")),
  "enabling-tools": loadTopic(() => import("@/components/docs/topics/enabling-tools")),
  "setting-routines": loadTopic(() => import("@/components/docs/topics/setting-routines")),
  "pulse": loadTopic(() => import("@/components/docs/topics/pulse")),
  "ai-recommendations": loadTopic(() => import("@/components/docs/topics/ai-recommendations")),
  "weekly-briefs": loadTopic(() => import("@/components/docs/topics/weekly-briefs")),
  "using-the-dashboard": loadTopic(() => import("@/components/docs/topics/using-the-dashboard")),
  "insights": loadTopic(() => import("@/components/docs/topics/insights")),
  "integration-data-guide": loadTopic(() => import("@/components/docs/topics/integration-data-guide")),
  "data-freshness": loadTopic(() => import("@/components/docs/topics/data-freshness")),
  "custom-dashboards": loadTopic(() => import("@/components/docs/topics/custom-dashboards")),
  "printing": loadTopic(() => import("@/components/docs/topics/printing")),
  "navigating-clients": loadTopic(() => import("@/components/docs/topics/navigating-clients")),
  "managing-reports": loadTopic(() => import("@/components/docs/topics/managing-reports")),
  "creating-reports": loadTopic(() => import("@/components/docs/topics/creating-reports")),
  "custom-reports": loadTopic(() => import("@/components/docs/topics/custom-reports")),
  "sharing-emailing-reports": loadTopic(() => import("@/components/docs/topics/sharing-emailing-reports")),
  "scheduled-reports": loadTopic(() => import("@/components/docs/topics/scheduled-reports")),
  "campaigns": loadTopic(() => import("@/components/docs/topics/campaigns")),
  "social-publishing": loadTopic(() => import("@/components/docs/topics/social-publishing")),
  "sitemaps": loadTopic(() => import("@/components/docs/topics/sitemaps")),
  "media-library": loadTopic(() => import("@/components/docs/topics/media-library")),
  "keyword-tracker": loadTopic(() => import("@/components/docs/topics/keyword-tracker")),
  "seo-research": loadTopic(() => import("@/components/docs/topics/seo-research")),
  "competitor-profiles": loadTopic(() => import("@/components/docs/topics/competitor-profiles")),
  "aeo-research": loadTopic(() => import("@/components/docs/topics/aeo-research")),
  "path-explorer": loadTopic(() => import("@/components/docs/topics/path-explorer")),
  "deep-research": loadTopic(() => import("@/components/docs/topics/deep-research")),
  "uptime-monitoring": loadTopic(() => import("@/components/docs/topics/uptime-monitoring")),
  "site-vitals": loadTopic(() => import("@/components/docs/topics/site-vitals")),
  "export-for-ai": loadTopic(() => import("@/components/docs/topics/export-for-ai")),
  "demo-mode": loadTopic(() => import("@/components/docs/topics/demo-mode")),
  "website-list": loadTopic(() => import("@/components/docs/topics/website-list")),
  "agency-uptime": loadTopic(() => import("@/components/docs/topics/agency-uptime")),
  "api-costs": loadTopic(() => import("@/components/docs/topics/api-costs")),
  "changelog": loadTopic(() => import("@/components/docs/topics/changelog")),
  "managing-users": loadTopic(() => import("@/components/docs/topics/managing-users")),
  "client-mode": loadTopic(() => import("@/components/docs/topics/client-mode")),
  "licenses": loadTopic(() => import("@/components/docs/topics/licenses")),
  "developer-tools": loadTopic(() => import("@/components/docs/topics/developer-tools")),
  "slack-integration": loadTopic(() => import("@/components/docs/topics/slack-integration")),
  "mcp-server": loadTopic(() => import("@/components/docs/topics/mcp-server")),
  "google-analytics": loadTopic(() => import("@/components/docs/topics/google-analytics")),
  "google-search-console": loadTopic(() => import("@/components/docs/topics/google-search-console")),
  "google-ads": loadTopic(() => import("@/components/docs/topics/google-ads")),
  "google-business-profile": loadTopic(() => import("@/components/docs/topics/google-business-profile")),
  "meta-facebook": loadTopic(() => import("@/components/docs/topics/meta-facebook")),
  "meta-ads": loadTopic(() => import("@/components/docs/topics/meta-ads")),
  "instagram": loadTopic(() => import("@/components/docs/topics/instagram")),
  "linkedin": loadTopic(() => import("@/components/docs/topics/linkedin")),
  "stackadapt": loadTopic(() => import("@/components/docs/topics/stackadapt")),
  "callrail": loadTopic(() => import("@/components/docs/topics/callrail")),
  "hubspot": loadTopic(() => import("@/components/docs/topics/hubspot")),
  "hubspot-email": loadTopic(() => import("@/components/docs/topics/hubspot-email")),
  "wordpress": loadTopic(() => import("@/components/docs/topics/wordpress")),
  "joomla": loadTopic(() => import("@/components/docs/topics/joomla")),
  "cloudways": loadTopic(() => import("@/components/docs/topics/cloudways")),
  "performance-audit": loadTopic(() => import("@/components/docs/topics/performance-audit")),
  "security-tech-audit": loadTopic(() => import("@/components/docs/topics/security-tech-audit")),
  "seo-audit": loadTopic(() => import("@/components/docs/topics/seo-audit")),
  "link-checker": loadTopic(() => import("@/components/docs/topics/link-checker")),
  "ai-search-health": loadTopic(() => import("@/components/docs/topics/ai-search-health")),
  "accessibility-audit": loadTopic(() => import("@/components/docs/topics/accessibility-audit")),
  "client-portal-guide": loadTopic(() => import("@/components/docs/topics/client-portal-guide")),
  "publishing-approvals": loadTopic(() => import("@/components/docs/topics/publishing-approvals")),
  "my-account": loadTopic(() => import("@/components/docs/topics/my-account")),
  "global-search-create": loadTopic(() => import("@/components/docs/topics/global-search-create")),
  "data-dictionary": loadTopic(() => import("@/components/docs/topics/data-dictionary")),
  "integration-troubleshooting": loadTopic(() => import("@/components/docs/topics/integration-troubleshooting")),
  "data-security": loadTopic(() => import("@/components/docs/topics/data-security")),
};

const FEATURED_SLUGS = [
  "getting-started",
  "using-the-dashboard",
  "creating-reports",
  "pulse",
  "social-publishing",
  "website-list",
];

const FEATURED_TOPICS = FEATURED_SLUGS.flatMap((slug) => {
  const topic = getTopicBySlug(slug);
  return topic ? [{ topic, matches: [], score: 0 }] : [];
});

function SearchIcon() {
  return <span className="text-spark-gray">{icons.search}</span>;
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

function ExpandIcon({ expanded }: { expanded: boolean }) {
  return expanded ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M8 3v5H3M16 3v5h5M8 21v-5H3M16 21v-5h5" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" />
    </svg>
  );
}

interface HelpCenterProps {
  name: string | null;
  role: string;
  variant?: "topbar" | "drawer";
}

/** HubSpot-style in-app documentation search and article reader. */
export function HelpCenter({ name, role, variant = "topbar" }: HelpCenterProps) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const readerRef = useRef<HTMLDivElement>(null);

  const closeHelp = useCallback(() => {
    setOpen(false);
    setExpanded(false);
    setQuery("");
    setSelectedSlug(null);
    setActiveIndex(0);
    requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        closeHelp();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, closeHelp]);

  useEffect(() => {
    if (!open || selectedSlug) return;
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open, selectedSlug]);

  const readableTopics = useMemo(
    () => DOC_TOPICS.filter((topic) => canReadDocTopic(topic, role)),
    [role],
  );
  const featuredTopics = useMemo(
    () => FEATURED_TOPICS.filter(({ topic }) => canReadDocTopic(topic, role)),
    [role],
  );
  const searchResults = useMemo(
    () => query.trim() ? searchDocumentation(query, readableTopics).slice(0, 12) : featuredTopics,
    [query, readableTopics, featuredTopics],
  );
  const selectedTopic = selectedSlug ? getTopicBySlug(selectedSlug) : undefined;
  const TopicContent = selectedSlug ? TOPIC_COMPONENTS[selectedSlug] : undefined;
  const relatedTopics = selectedTopic ? getRelatedTopics(selectedTopic, role) : [];
  const firstName = name?.trim().split(/\s+/)[0];

  function selectTopic(topic: DocTopic) {
    if (!canReadDocTopic(topic, role)) return;
    setSelectedSlug(topic.slug);
    requestAnimationFrame(() => {
      if (readerRef.current) readerRef.current.scrollTop = 0;
    });
  }

  function onSearchKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    event.stopPropagation();
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => searchResults.length ? (index + 1) % searchResults.length : 0);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => searchResults.length ? (index - 1 + searchResults.length) % searchResults.length : 0);
    } else if (event.key === "Enter" && searchResults[activeIndex]) {
      event.preventDefault();
      selectTopic(searchResults[activeIndex].topic);
    }
  }

  function onArticleClick(event: ReactMouseEvent<HTMLDivElement>) {
    if (!(event.target instanceof Element)) return;
    const anchor = event.target.closest("a");
    const href = anchor?.getAttribute("href");
    if (!href?.startsWith("/docs/")) return;
    const slug = href.slice("/docs/".length).split(/[?#]/)[0];
    const topic = getTopicBySlug(slug);
    if (!topic) return;
    event.preventDefault();
    selectTopic(topic);
  }

  const dialog = open && typeof document !== "undefined"
    ? createPortal(
        <div
          className={`fixed inset-0 z-[110] flex items-start justify-end bg-black/15 ${
            expanded ? "p-4" : "p-2 pt-[calc(var(--topbar-h)+0.5rem)] md:p-4 md:pt-[calc(var(--topbar-h)+0.75rem)]"
          }`}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeHelp();
          }}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="help-dialog-title"
            className={`flex min-h-0 flex-col overflow-hidden rounded-xl border border-spark-light-gray bg-white shadow-[0_24px_64px_-16px_rgba(16,24,40,0.36),0_8px_20px_-6px_rgba(16,24,40,0.18)] transition-[width,height] ${
              expanded ? "h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-none" : "h-[46rem] w-full max-w-[43rem]"
            }`}
            style={expanded ? undefined : { maxHeight: "calc(100vh - var(--topbar-h) - 1.5rem)" }}
          >
            <div className="relative flex h-12 shrink-0 items-center justify-end border-b border-spark-light-gray px-4">
              <span className="absolute left-1/2 top-2 -translate-x-1/2 text-[14px] leading-3 tracking-[3px] text-spark-gray" aria-hidden>
                •••<br />•••
              </span>
              <button
                type="button"
                onClick={() => setExpanded((value) => !value)}
                aria-label={expanded ? "Restore help window" : "Expand help window"}
                title={expanded ? "Restore" : "Expand"}
                className="flex h-8 w-8 items-center justify-center rounded-md text-spark-gray transition-colors hover:bg-spark-hover hover:text-spark-black"
              >
                <ExpandIcon expanded={expanded} />
              </button>
              <button
                type="button"
                onClick={closeHelp}
                aria-label="Close help"
                title="Close"
                className="flex h-8 w-8 items-center justify-center rounded-md text-spark-gray transition-colors hover:bg-spark-hover hover:text-spark-black"
              >
                <CloseIcon />
              </button>
            </div>

            {selectedTopic && TopicContent ? (
              <>
                <div className="shrink-0 border-b border-spark-light-gray px-5 py-4 md:px-8">
                  <button
                    type="button"
                    onClick={() => setSelectedSlug(null)}
                    className="mb-3 flex items-center gap-1.5 text-xs font-semibold text-spark-blue-text hover:underline"
                  >
                    {icons.arrowLeft}
                    Back to results
                  </button>
                  <p className="mb-1 text-[11px] font-bold uppercase tracking-[.08em] text-spark-gray">
                    {selectedTopic.category}
                  </p>
                  <h2 id="help-dialog-title" className="text-xl font-bold text-spark-black md:text-2xl">
                    {selectedTopic.title}
                  </h2>
                </div>
                <div
                  ref={readerRef}
                  onClick={onArticleClick}
                  id={`help-article-${selectedTopic.slug}`}
                  className="min-h-0 flex-1 overflow-y-auto px-5 pb-8 pt-1 md:px-8"
                >
                  <DocTableOfContents rootId={`help-article-${selectedTopic.slug}`} />
                  <TopicContent />
                  <DocArticleFooter
                    key={`footer-${selectedTopic.slug}`}
                    slug={selectedTopic.slug}
                    updatedAt={selectedTopic.updatedAt ?? DEFAULT_DOC_REVIEW_DATE}
                    related={relatedTopics}
                  />
                </div>
                <div className="shrink-0 border-t border-spark-light-gray bg-spark-hover px-5 py-3 text-right md:px-8">
                  <Link
                    href={`/docs/${selectedTopic.slug}`}
                    onClick={closeHelp}
                    className="text-xs font-semibold text-spark-blue-text hover:underline"
                  >
                    Open in full documentation →
                  </Link>
                </div>
              </>
            ) : (
              <>
                <div className="shrink-0 px-5 pb-5 pt-6 md:px-8 md:pt-8">
                  <h2 id="help-dialog-title" className="text-2xl font-bold text-spark-black">
                    {firstName ? `Hi ${firstName}, how can we help?` : "How can we help?"}
                  </h2>
                  <div className="relative mt-4">
                    <span aria-hidden className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2">
                      <SearchIcon />
                    </span>
                    <input
                      ref={inputRef}
                      type="search"
                      role="combobox"
                      aria-expanded="true"
                      aria-controls="help-search-results"
                      aria-activedescendant={searchResults.length ? `help-result-${activeIndex}` : undefined}
                      aria-autocomplete="list"
                      placeholder="Search the documentation"
                      value={query}
                      onChange={(event) => {
                        setQuery(event.target.value);
                        setActiveIndex(0);
                      }}
                      onKeyDown={onSearchKeyDown}
                      className="h-12 w-full rounded-full border border-spark-gray bg-white pl-11 pr-5 text-sm text-spark-black shadow-sm outline-none transition focus:border-spark-blue"
                    />
                  </div>
                </div>

                <div id="help-search-results" role="listbox" className="min-h-0 flex-1 overflow-y-auto border-t border-spark-light-gray px-3 py-3 md:px-6">
                  <p className="px-3 pb-2 text-[11px] font-bold uppercase tracking-[.08em] text-spark-gray">
                    {query.trim()
                      ? `${searchResults.length} ${searchResults.length === 1 ? "result" : "results"}`
                      : "Popular documentation"}
                  </p>

                  {searchResults.map((result: DocTopicMatch, index) => (
                    <button
                      key={result.topic.slug}
                      id={`help-result-${index}`}
                      type="button"
                      role="option"
                      aria-selected={index === activeIndex}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => selectTopic(result.topic)}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors ${
                        index === activeIndex ? "bg-spark-hover" : "hover:bg-spark-hover"
                      }`}
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-spark-blue/10 text-spark-blue-text">
                        {icons.book}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-spark-black">
                          {highlight(result.topic.title, result.matches).map((part, partIndex) =>
                            part.bold ? (
                              <strong key={partIndex} className="font-extrabold text-spark-blue-text">{part.text}</strong>
                            ) : (
                              <span key={partIndex}>{part.text}</span>
                            ),
                          )}
                        </span>
                        <span className="mt-0.5 block text-xs text-spark-gray">{result.topic.category}</span>
                      </span>
                      <span className="shrink-0 text-spark-gray">{icons.chevronRight}</span>
                    </button>
                  ))}

                  {query.trim() && searchResults.length === 0 && (
                    <div className="px-4 py-10 text-center">
                      <p className="text-sm font-semibold text-spark-black">No documentation matched “{query.trim()}”</p>
                      <p className="mt-1 text-xs text-spark-gray">Try a feature name or a shorter question.</p>
                    </div>
                  )}
                </div>

                <div className="shrink-0 border-t border-spark-light-gray bg-spark-hover px-5 py-3 text-right md:px-8">
                  <Link href="/docs" onClick={closeHelp} className="text-xs font-semibold text-spark-blue-text hover:underline">
                    Browse all documentation →
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        title="Help"
        aria-label="Help and documentation"
        aria-haspopup="dialog"
        aria-expanded={open}
        className={variant === "drawer"
          ? "flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium text-[#C3CAD3] transition-colors hover:bg-white/[.06] hover:text-white"
          : "flex h-8 w-8 items-center justify-center rounded-md text-[#AAB2BD] transition-colors hover:bg-white/[.06] hover:text-white"
        }
        >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        {variant === "drawer" && <span>Help</span>}
      </button>
      {dialog}
    </>
  );
}
